import React from 'react';
import { API_TOKENS_UPDATED, ApiToken, useApiTokensService } from 'nekst-api';
import {
  Cell,
  List,
  ListDataRefresher,
  TextField,
  DateField,
  useShowContextRequired,
  usePopupManager, TextLink,
  Text, NewLine,
} from 'ui-builder';
import InnerBlock from '../../shared/web/layout/InnerBlock';
import { CreateApiTokenButton } from './CreateApiTokenForm';
import {
  CopyValueToClipboard,
  GridLayout,
  IconButton,
  IconDeleteButton,
  IconType,
  Popup
} from 'features/nekst-widgets';
import InformationBox from '../../shared/web/layout/InformationBox';
import styles from './ApiTokensList.module.scss';

function ShowTokenButton() {
  const apiTokensService = useApiTokensService();
  const showContext = useShowContextRequired<ApiToken>();
  const popupManager = usePopupManager();
  const token = showContext.data;

  const handleClick = async () => {
    const fullToken = await apiTokensService.getToken(token.id);
    popupManager.openPopup(
      <Popup
        title="API Token"
        content={(
          <div style={{ wordBreak: 'break-all' }}>
            {fullToken.apiToken}
            {' '}
            <CopyValueToClipboard text={fullToken.apiToken} />
          </div>
        )}
      />
    );
  };

  if (!showContext.data.isExpired) {
    return (
      <IconButton
        onClick={handleClick}
        type={IconType.EYE}
        title="Show Token"
      />
    );
  } else {
    return null;
  }
}

function IsExpiredField() {
  const showContext = useShowContextRequired<ApiToken>();

  if (showContext.data.isExpired) {
    return (
      <Text className={styles.label}>
        Expired
      </Text>
    );
  } else {
    return null;
  }
}

function DeleteApiTokenButton() {
  const apiTokensService = useApiTokensService();
  const showContext = useShowContextRequired<ApiToken>();
  const token = showContext.data;

  return (
    <IconDeleteButton
      deleteFunc={async () => {
        await apiTokensService.deleteToken(token.id);
      }}
      message={`Are you sure you want to delete API token "${token.name}"?`}
    />
  );
}

export default function ApiTokensList() {
  const apiTokensService = useApiTokensService();

  return (
    <InnerBlock title="API Tokens" headerRight={<CreateApiTokenButton />}>
      <InformationBox text={<>
        Create API Tokens for <TextLink onClick={() => {
        window.open('https://api.nekst.com/api/doc', '_blank');
      }}>Nekst Public API</TextLink>
      </>} />
      <List getDataFunc={apiTokensService.getApiTokens}>
        <GridLayout
          weights={[4, 4, 2, 2]}
          noDataMessage="No API tokens have been created yet."
        >
          <Cell label="Name">
            <TextField source="name" label="Name" />
          </Cell>
          <Cell label="Token">
            <TextField source="apiToken" label="Token" />
            <ShowTokenButton />
          </Cell>
          <Cell label="Expires At">
            <DateField source="expiresAt" label="Expires" noValueText="-" />
            <NewLine />
            <IsExpiredField />
          </Cell>
          <Cell label="Actions">
            <DeleteApiTokenButton />
          </Cell>
        </GridLayout>
        <ListDataRefresher eventName={API_TOKENS_UPDATED} />
      </List>
    </InnerBlock>
  );
}
